import {
  ActivityIndicator,
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { ArrowRight } from "lucide-react-native";
import {
  CardSurface,
  FEATURE_CARD_RADIUS,
  GlassButton,
  Tag,
} from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";

type CardField = {
  label: string;
  value: string;
};

type Props = {
  category: string;
  status: string;
  title: string;
  subtitle: string;
  fields: [CardField, CardField];
  actionLabel?: string;
  onAction?: () => void;
  loading?: boolean;
};

// This is the same silk background and glass treatment used by the homepage
// and Care medical-history card. It is kept as a comparison variant until a
// direction is selected for the real Coverage & Benefits surface.
const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

export default function TexturedCoverageCard({
  category,
  status,
  title,
  subtitle,
  fields,
  actionLabel,
  onAction,
  loading = false,
}: Props) {
  return (
    <CardSurface
      variant="feature"
      style={{
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
      }}
    >
      <Image
        source={silkBrown}
        resizeMode="cover"
        style={StyleSheet.absoluteFill}
      />
      <View
        style={{
          minHeight: 244,
          padding: 17,
          backgroundColor: semanticColors.onDark.fill.subtle,
          borderRadius: FEATURE_CARD_RADIUS,
          borderWidth: 1,
          borderColor: semanticColors.onDark.fill.strong,
          ...(Platform.OS === "web"
            ? ({
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
              } as Record<string, string>)
            : {}),
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 19,
                lineHeight: 24,
                fontWeight: "700",
                letterSpacing: -0.3,
                color: semanticColors.onDark.text.primary,
              }}
            >
              {category}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                marginTop: 4,
                fontSize: 11.5,
                lineHeight: 16,
                fontWeight: "500",
                color: semanticColors.onDark.text.secondary,
              }}
            >
              {subtitle}
            </Text>
          </View>
          <Tag
            label={status}
            tone="onDark"
            shape="pill"
            iconOnly={loading}
            leading={
              loading ? (
                <ActivityIndicator
                  size="small"
                  color={semanticColors.onDark.text.secondary}
                />
              ) : undefined
            }
          />
        </View>

        <Text
          className="font-dm-sans"
          numberOfLines={2}
          style={{
            marginTop: 22,
            fontSize: 21,
            lineHeight: 27,
            fontWeight: "700",
            letterSpacing: -0.3,
            color: semanticColors.onDark.text.primary,
          }}
        >
          {title}
        </Text>

        <View style={{ flexDirection: "row", gap: 20, marginTop: 18 }}>
          {fields.map((field) => (
            <View key={field.label} style={{ flex: 1, minWidth: 0 }}>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  lineHeight: 13,
                  fontWeight: "600",
                  color: semanticColors.onDark.text.tertiary,
                  letterSpacing: 0.35,
                  textTransform: "uppercase",
                }}
              >
                {field.label}
              </Text>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  lineHeight: 18,
                  fontWeight: "600",
                  color: semanticColors.onDark.text.primary,
                }}
              >
                {field.value}
              </Text>
            </View>
          ))}
        </View>

        {actionLabel ? (
          <GlassButton
            label={actionLabel}
            action="textured_coverage_card_action"
            size="large"
            fill
            trailing={
              <ArrowRight
                size={15}
                color={semanticColors.onDark.text.primary}
                strokeWidth={2}
              />
            }
            onPress={onAction}
            disabled={!onAction}
            style={{ marginTop: "auto" }}
          />
        ) : null}
      </View>
    </CardSurface>
  );
}
